/**
 * Stylised mini mockups of the real product screens for the public site.
 *
 * Built from HTML + CSS (no screenshots, no raster art) so they stay crisp at
 * any size and cost a few kB. Content mirrors what the screens actually show,
 * with realistic game data (Northmont vs. Wayne). Every mockup is decorative
 * (`aria-hidden`): the section copy next to it carries the meaning.
 *
 * Motion lives in marketing.css and only runs while the mockup is in view
 * (`.is-inview`, set by `useRevealOnScroll`) and never under
 * `prefers-reduced-motion`; the static state is the finished state.
 */
import type { CSSProperties, ReactNode } from 'react';

import { Football } from './field';

function Device({
  kind,
  title,
  meta,
  children,
}: {
  kind: 'tablet' | 'desktop' | 'phone';
  title: string;
  meta?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`mock mock--${kind}`} data-reveal aria-hidden="true">
      <div className="mock__bar">
        <span className="mock__logo" />
        <span className="mock__title">{title}</span>
        {meta === undefined ? null : <span className="mock__meta">{meta}</span>}
      </div>
      <div className="mock__body">{children}</div>
    </div>
  );
}

function Chips({
  label,
  options,
  selected,
  step,
}: {
  label: string;
  options: readonly string[];
  selected: string;
  /** Order in the tap sequence (drives the animation delay). */
  step: number;
}): JSX.Element {
  return (
    <div className="pe-card">
      <span className="pe-card__label">{label}</span>
      <div className="pe-card__grid">
        {options.map((option) => (
          <span
            key={option}
            className={`pe-chip${option === selected ? ' pe-chip--on' : ''}`}
            style={option === selected ? ({ '--step': step } as CSSProperties) : undefined}
          >
            {option}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- 1. entry

export function PlayEntryMock(): JSX.Element {
  return (
    <Device kind="tablet" title="Play Entry" meta="Northmont vs. Wayne · Q2 4:12">
      <div className="pe-situation">
        <span className="pe-situation__dd">2nd &amp; 7</span>
        <span>Ball on N34</span>
        <span>Left hash</span>
        <span className="pe-situation__ops">
          <i />
          <i />2 operators
        </span>
      </div>
      <div className="pe-board">
        <Chips
          label="Formation"
          options={['Trips Rt', 'Doubles', 'Ace', 'Empty', 'I-Right', 'Bunch Lt']}
          selected="Trips Rt"
          step={0}
        />
        <Chips
          label="Concept"
          options={['Power Right', 'Inside Zone', 'Counter', 'Mesh', 'Stick', 'Verticals']}
          selected="Power Right"
          step={1}
        />
        <Chips
          label="Opp front"
          options={['4-3 Over', '4-2-5', '3-4 Odd', 'Bear']}
          selected="4-2-5"
          step={2}
        />
        <div className="pe-card pe-card--result">
          <span className="pe-card__label">Result</span>
          <div className="pe-gain">
            <span className="pe-gain__btn">−</span>
            <span className="pe-gain__value">
              <b className="pe-gain__from">0</b>
              <b className="pe-gain__to">+6</b>
            </span>
            <span className="pe-gain__btn">+</span>
          </div>
          <div className="pe-card__grid">
            <span className="pe-chip pe-chip--on" style={{ '--step': 3 } as CSSProperties}>
              Rush
            </span>
            <span className="pe-chip">Pass</span>
            <span className="pe-chip">Penalty</span>
          </div>
        </div>
      </div>
      <div className="pe-footer">
        <span className="pe-footer__sync">Synced</span>
        <span className="pe-save">Save play 38</span>
        <span className="pe-toast">Play 38 saved · 3rd &amp; 1 at N40</span>
      </div>
    </Device>
  );
}

// ------------------------------------------------------ 2. summary + video

const QUARTERS = [
  { team: 'Northmont', short: 'NOR', q: ['7', '7', '7', '–'], total: '21' },
  { team: 'Wayne', short: 'WAY', q: ['3', '7', '7', '–'], total: '17' },
];

const DRIVE_LOG = [
  { q: 'Q3', play: '1st & 10 at N28', call: 'Inside Zone', gain: '+6' },
  { q: 'Q3', play: '2nd & 4 at N34', call: 'Mesh', gain: 'Inc' },
  { q: 'Q3', play: '3rd & 4 at N34', call: 'Stick', gain: '+7' },
  { q: 'Q3', play: '1st & 10 at N41', call: 'Power Right', gain: '+6' },
];

export function GameSummaryMock(): JSX.Element {
  return (
    <div className="mock-stack">
      <Device kind="desktop" title="Game Summary" meta="Live">
        <div className="gs-board">
          <div className="gs-team">
            <span className="gs-team__name">Northmont</span>
            <span className="gs-team__score">21</span>
          </div>
          <div className="gs-clock">
            <span>Q3</span>
            <b>6:48</b>
            <span className="gs-dd">
              <span className="gs-flip gs-flip--from">1st &amp; 10 · N41</span>
              <span className="gs-flip gs-flip--to">2nd &amp; 4 · N47</span>
            </span>
          </div>
          <div className="gs-team gs-team--away">
            <span className="gs-team__score">17</span>
            <span className="gs-team__name">Wayne</span>
          </div>
        </div>
        <table className="gs-quarters">
          <thead>
            <tr>
              <th />
              <th>1</th>
              <th>2</th>
              <th>3</th>
              <th>4</th>
              <th>T</th>
            </tr>
          </thead>
          <tbody>
            {QUARTERS.map((row) => (
              <tr key={row.team}>
                <th>{row.short}</th>
                {row.q.map((score, index) => (
                  <td key={String(index)}>{score}</td>
                ))}
                <td className="gs-quarters__total">{row.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="gs-log">
          <span className="gs-log__title">Drive log</span>
          {DRIVE_LOG.map((row, index) => (
            <div
              className={`gs-log__row${index === DRIVE_LOG.length - 1 ? ' gs-log__row--new' : ''}`}
              key={`${row.play}-${row.call}`}
            >
              <span>{row.q}</span>
              <span>{row.play}</span>
              <span>{row.call}</span>
              <b className={row.gain.startsWith('+') ? 'is-plus' : ''}>{row.gain}</b>
            </div>
          ))}
        </div>
        <div className="gs-units">
          {[
            ['Rush', '24-131'],
            ['Pass', '9/14 118'],
            ['3rd dn', '4/7'],
            ['Success', '54%'],
          ].map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <b>{value}</b>
            </div>
          ))}
        </div>
      </Device>
      <div className="mock mock--video" data-reveal aria-hidden="true">
        <div className="lv-screen">
          <svg viewBox="0 0 320 180" preserveAspectRatio="none" className="lv-field">
            <defs>
              <linearGradient id="gt-lv-turf" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#1d5a2b" />
                <stop offset="1" stopColor="#0f3519" />
              </linearGradient>
            </defs>
            <rect width="320" height="180" fill="url(#gt-lv-turf)" />
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <line
                key={i}
                x1={30 + i * 45}
                y1="0"
                x2={-20 + i * 60}
                y2="180"
                stroke="#e9f2e9"
                strokeOpacity="0.5"
                strokeWidth="1.2"
              />
            ))}
            {[
              [150, 92],
              [162, 86],
              [170, 98],
              [140, 100],
              [178, 90],
              [158, 104],
              [134, 88],
            ].map(([x, y]) => (
              <circle key={`o${x}-${y}`} cx={x} cy={y} r="3.2" fill="#ffffff" />
            ))}
            {[
              [196, 90],
              [200, 100],
              [204, 82],
              [214, 96],
              [222, 108],
              [190, 112],
            ].map(([x, y]) => (
              <circle key={`d${x}-${y}`} cx={x} cy={y} r="3.2" fill="#f2b234" />
            ))}
          </svg>
          <span className="lv-live">Live</span>
          <span className="lv-timeline">
            <i />
          </span>
        </div>
        <div className="lv-rail">
          <span className="lv-rail__title">Stat rail</span>
          <div>
            <span>Last play</span>
            <b>Power Right +6</b>
          </div>
          <div>
            <span>Drive</span>
            <b>4 plays, 19 yds</b>
          </div>
          <div>
            <span>Rush avg</span>
            <b>5.5</b>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- 3. highlight

const COVERAGE = [
  { label: 'Cover 3', pct: 44 },
  { label: 'Cover 1', pct: 28 },
  { label: 'Quarters', pct: 18 },
  { label: 'Cover 2', pct: 10 },
];

const DOWNS = [
  { label: '1st', run: 62 },
  { label: '2nd', run: 48 },
  { label: '3rd', run: 30 },
];

export function HighlightMock(): JSX.Element {
  return (
    <Device kind="tablet" title="Coaches Highlight" meta="Offense · vs. Wayne">
      <div className="hl-filters">
        <span className="hl-pill hl-pill--on">Offense</span>
        <span className="hl-pill">Defense</span>
        <span className="hl-pill hl-pill--filter">3rd down ×</span>
      </div>
      <div className="hl-grid">
        <div className="hl-card hl-card--metric">
          <span className="hl-card__title">Success rate</span>
          <b className="hl-big">54%</b>
          <span className="hl-sub">27 plays · 3 not logged</span>
        </div>
        <div className="hl-card hl-card--metric">
          <span className="hl-card__title">3rd down</span>
          <b className="hl-big">4/7</b>
          <span className="hl-sub">57% converted</span>
        </div>
        <div className="hl-card hl-card--wide">
          <span className="hl-card__title">Opponent coverage</span>
          {COVERAGE.map((row, index) => (
            <div className="hl-bar" key={row.label}>
              <span>{row.label}</span>
              <span className="hl-bar__track">
                <i style={{ '--pct': `${row.pct}%`, '--n': index } as CSSProperties} />
              </span>
              <b>{row.pct}%</b>
            </div>
          ))}
        </div>
        <div className="hl-card hl-card--wide">
          <span className="hl-card__title">Run / pass by down</span>
          {DOWNS.map((row, index) => (
            <div className="hl-split" key={row.label}>
              <span>{row.label}</span>
              <span className="hl-split__track">
                <i style={{ '--pct': `${row.run}%`, '--n': index } as CSSProperties} />
              </span>
              <b>
                {row.run}/{100 - row.run}
              </b>
            </div>
          ))}
        </div>
        <div className="hl-card hl-card--trend">
          <span className="hl-card__title">Yards per play, by drive</span>
          <svg viewBox="0 0 200 60" preserveAspectRatio="none" className="hl-trend">
            <polyline
              points="0,44 28,36 56,40 84,22 112,30 140,16 168,20 200,8"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
              pathLength={1}
            />
          </svg>
        </div>
      </div>
    </Device>
  );
}

// ------------------------------------------------------------------ 4. stats

const STAT_ROWS = [
  ['#22 M. Carter', 'RB', '18', '104', '5.8', '1'],
  ['#7 J. Ellis', 'QB', '6', '27', '4.5', '0'],
  ['#31 D. Owens', 'RB', '5', '19', '3.8', '1'],
  ['#4 T. Brooks', 'WR', '2', '11', '5.5', '0'],
];

export function StatTrackerMock(): JSX.Element {
  return (
    <Device kind="desktop" title="Stat Tracker" meta="Rushing">
      <div className="st-toolbar">
        <span className="st-seg">
          <span className="st-seg__on">Game</span>
          <span>Season</span>
        </span>
        <span className="st-btn">Sort and filter</span>
        <span className="st-btn">Columns</span>
        <span className="st-btn st-btn--export">Export · CSV / XLSX</span>
      </div>
      <table className="st-table">
        <thead>
          <tr>
            <th>Player</th>
            <th>Pos</th>
            <th>Att</th>
            <th>Yds</th>
            <th>Avg</th>
            <th>TD</th>
          </tr>
        </thead>
        <tbody>
          {STAT_ROWS.map((row, index) => (
            <tr key={row[0]} style={{ '--n': index } as CSSProperties}>
              {row.map((cell, cellIndex) => (
                <td key={`${String(cellIndex)}-${cell}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Team</td>
            <td />
            <td>31</td>
            <td>161</td>
            <td>5.2</td>
            <td>2</td>
          </tr>
        </tfoot>
      </table>
      <div className="st-history">
        <span className="st-history__tag">Edited</span>
        Play 24 · gain changed +4 → +5 by the primary stat operator
      </div>
    </Device>
  );
}

// ------------------------------------------------------------------- 5. hudl

const CONCEPTS = [
  ['Power Right', 14],
  ['Inside Zone', 11],
  ['Mesh', 8],
  ['Stick', 6],
  ['Counter', 4],
] as const;

export function HudlMock(): JSX.Element {
  return (
    <div className="mock-stack mock-stack--hudl">
      <Device kind="desktop" title="Hudl" meta="Import · Export">
        <div className="hu-file">
          <span className="hu-file__icon">XLSX</span>
          <div className="hu-file__meta">
            <b>Wayne_Week6_playlist.xlsx</b>
            <span className="hu-progress">
              <i />
            </span>
            <span className="hu-file__note">58 rows matched to plays 1–58 in order</span>
          </div>
        </div>
        <div className="hu-cols">
          {[
            'PLAY #',
            'ODK',
            'DN',
            'DIST',
            'YARD LN',
            'HASH',
            'OFF FORM',
            'OFF PLAY',
            'RESULT',
            'GN/LS',
          ].map((column) => (
            <span key={column}>{column}</span>
          ))}
        </div>
        <div className="hu-export">
          <span className="hu-export__label">Export this game as</span>
          <span className="hu-choice hu-choice--on">Hudl-compatible</span>
          <span className="hu-choice">Template: Coordinator sheet</span>
        </div>
      </Device>
      <div className="mock mock--card pb" data-reveal aria-hidden="true">
        <span className="pb__title">Playbook · Run concepts</span>
        {CONCEPTS.map(([name, count], index) => (
          <div className="pb__row" key={name}>
            <span>{name}</span>
            <span className={`pb__kind${index % 2 === 0 ? '' : ' pb__kind--team'}`}>
              {index % 2 === 0 ? 'Stock' : 'Team'}
            </span>
            <b>{count}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- 6. fans

export function FanPhoneMock(): JSX.Element {
  return (
    <div className="phone" data-reveal aria-hidden="true">
      <span className="phone__notch" />
      <div className="phone__screen">
        <div className="phone__head">
          <span>Northmont</span>
          <span className="phone__follow">Following ✓</span>
        </div>
        <div className="phone__video">
          <span className="lv-live">Live</span>
          <Football className="phone__ball" />
        </div>
        <div className="phone__score">
          <div>
            <span>NOR</span>
            <b>21</b>
          </div>
          <div className="phone__clock">
            <span>Q3 · 6:48</span>
            <span>2nd &amp; 4 · N47</span>
          </div>
          <div>
            <b>17</b>
            <span>WAY</span>
          </div>
        </div>
        <div className="phone__feed">
          <span className="phone__feed-title">Game summary</span>
          <p>
            <b>+6</b> Carter up the middle to the Northmont 47
          </p>
          <p>
            <b>+7</b> Ellis to Brooks on 3rd down, first down
          </p>
          <p>
            <b>TD</b> Carter 12-yd run · Northmont 21–17
          </p>
        </div>
      </div>
    </div>
  );
}
